import { useAppContext } from '@/components/AppContext'
import React, { useCallback, useEffect, useState } from 'react'
import { message, Modal } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table'
import SectionList from '../../components/sections/SectionList'
import {
  EditSection,
  ManageSectionItem
} from '@a4b/api/src/modules/Monetisation/Offerings/types'
import CreateSection from '../../components/sections/CreateSection'
import { parseDataOnEdit } from '../../utils/sections-helper'
import { A4B_APPS } from '@/utils/constants'
import { useSearchParams } from 'react-router-dom'
import { showErrorToast } from '@/utils/helper'

interface TabProps {
  currentTab: string
}
const ManageSectionScreen: React.FC<TabProps> = ({ currentTab }) => {
  const { networkInstance, app } = useAppContext()
  const monetisationPujaApi = app === A4B_APPS.SRIMANDIR ? networkInstance.clientWithHeaders.monetisationPujaApi : networkInstance.clientWithHeaders.monetisationAstroPujaApi

  const [sectionList, setSectionList] = useState<ManageSectionItem[]>([])
  const [showItemCreation, setShowItemCreation] = React.useState(false)
  const [isEditJourney, setIsEditJourney] = React.useState(false)
  const [showSectionEnableModal, setShowSectionEnableModal] = useState(false)
  const [showSectionDisableModal, setShowSectionDisableModal] = useState(false)
  const [paginationParams, setPaginationParams] =
    useState<TablePaginationConfig>({
      current: 1,
      pageSize: 10,
    })

  const [section, setSection] = useState<EditSection>()
  const [sectionForStatusUpdate, setSectionForStatusUpdate] =
    useState<ManageSectionItem>()
  const [searchParams, setSearchParams] = useSearchParams();
  const [sectionId, setSectionId] = useState<string | null>(searchParams.get('section_id'))

  const rowSelectionChange = (selectedRowKeys: any) => {
    setRowSelectionConfig({
      ...rowSelectionConfig,
      selectedRowKeys: [...selectedRowKeys],
    })
  }

  const [rowSelectionConfig, setRowSelectionConfig] = useState<any>({
    selectedRowKeys: [],
    onChange: rowSelectionChange,
  })

  const getSections = useCallback(async () => {
    try {
      const sections = await monetisationPujaApi.storesApi.getSections({
        limit: paginationParams.pageSize,
        offset:
          Number((paginationParams?.current || 1) - 1) * Number(paginationParams.pageSize),
      })
      setSectionList(sections?.data?.data?.sections)
      setPaginationParams({ ...paginationParams, total: sections?.data?.data?.count || 10 })
    } catch (error) {
      showErrorToast(error)
      // message.error({
      //   content: 'Error while fetching sections',
      //   duration: 3,
      // })
      console.log(error)
    }
  }, [monetisationPujaApi.storesApi, paginationParams?.current])

  useEffect(() => {
    if (currentTab !== "sections") {
      return;
    }
    getSections()
  }, [getSections, currentTab])

  const onCreateNew = () => {
    setShowItemCreation(true)
    setIsEditJourney(false)
    setSection(undefined)
  }

  const closeItemCreationForm = useCallback((fetchSection) => {
    setShowItemCreation(false)
    isEditJourney && setIsEditJourney(false)
    setSection(undefined)
    setSectionId(null)

    if (typeof fetchSection === 'boolean' && fetchSection) {
      setTimeout(() => {
        getSections()
      }, 500)
    }

    // Clear the section_id query param from the URL
    const queryParams = new URLSearchParams(searchParams.toString());
    queryParams.delete('section_id')
    setSearchParams(queryParams);
  }, [getSections, isEditJourney, searchParams, setSearchParams])

  const onStatusChange = async (section: ManageSectionItem) => {
    setSectionForStatusUpdate(section)
    if (section?.active) {
      setShowSectionDisableModal(true)
    } else {
      setShowSectionEnableModal(true)
    }
  }

  const fetchSectionById = useCallback(async (sectionId: string) => {
    try {
      const sectionResponse = await monetisationPujaApi.storesApi.getSectionById(sectionId)
      if (!sectionResponse) {
        return
      }

      const responseData = parseDataOnEdit(sectionResponse)
      setSection(responseData)
      setIsEditJourney(true)
      setShowItemCreation(true)
    } catch (e) {
      showErrorToast(e)
      //message.error('Error fetching Section By ID')
      console.log({ e })
    }
  }, [monetisationPujaApi.storesApi])

  useEffect(() => {
    if (sectionId) {
      fetchSectionById(sectionId)
    }
  }, [fetchSectionById, sectionId])

  const triggerEdit = useCallback((sectionItem: ManageSectionItem) => {
    if (!sectionItem?.id) {
      message.warning('Section ID not found', 2)
      return
    }

    if (sectionItem.id !== sectionId) {
      setSectionId(sectionItem.id)
      searchParams.set("section_id", sectionItem.id)
      setSearchParams(searchParams)
    }
  }, [searchParams, sectionId, setSearchParams])

  const onModalOk = useCallback(async () => {
    message.loading({
      content: 'Updating section status',
      duration: 1,
      key: 'update_section',
    })

    try {
      const sectionResponse =
        await monetisationPujaApi.storesApi.getSectionById(sectionForStatusUpdate?.id || '')
      const editForm: any = {
        ...sectionResponse?.data?.data,
      }
      const section_type = editForm?.section_type
      const item_type = editForm?.item_type

      if ((section_type === 'hero' || section_type === 'list_item_small' || section_type === 'list_item_large') && item_type === 'sku') {
        // const filteredStoreCard: any = editForm?.data?.map(
        //   (storeCard: any) => storeCard.id
        // )
        editForm["data"] = { ...editForm?.data }
      }
      if ((section_type === 'hero' || section_type === 'list_item_small' || section_type === 'list_item_large') && item_type === 'store') {
        // const filteredStoreList: any = editForm?.data?.store_ids?.map(
        //   (storeCard: any) => storeCard.id
        // )
        editForm["data"] = { ...editForm?.data }
      }
      if (section_type === 'category') {
        if (item_type === 'filters') {
          // For filters, data is an object with category_id and type
          // Keep the data structure as is
          editForm["data"] = { ...editForm?.data }
        } else {
          // For non-filters, data might be an array of categories
          if (Array.isArray(editForm?.data)) {
            const filteredCategories: any = editForm?.data?.map(
              (form_category: any) => {
                // Handle different possible structures
                if (form_category?.category?.id) {
                  return form_category.category.id
                } else if (form_category?.category_id) {
                  return form_category.category_id
                } else if (typeof form_category === 'string') {
                  return form_category
                } else {
                  return form_category?.id || form_category
                }
              }
            ).filter(Boolean) // Remove any undefined/null values
            editForm["data"] = filteredCategories
          } else {
            // If data is not an array, keep it as is
            editForm["data"] = editForm?.data
          }
        }
      }

      await monetisationPujaApi.storesApi.updateSection(
        sectionForStatusUpdate?.id || '',
        { ...editForm, active: !sectionResponse?.data?.data?.active },
      )
      setTimeout(() => {
        getSections()
      }, 500)
      setShowSectionEnableModal(false)
      setShowSectionDisableModal(false)
      message.success({
        content: 'Section card status updated sucessfully',
        duration: 3,
        key: 'update_section',
      })
    } catch (error) {
      setShowSectionEnableModal(false)
      setShowSectionDisableModal(false)
      showErrorToast(error)
      // message.error({
      //   content: 'Error while updating store card status',
      //   duration: 2,
      //   key: 'update_section',
      // })

      console.log(error)
    }
  }
    , [getSections, monetisationPujaApi.storesApi, sectionForStatusUpdate?.id])

  const onModalCancel = () => {
    setShowSectionEnableModal(false)
    setShowSectionDisableModal(false)
  }

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPaginationParams({
      ...pagination,
    })
  }

  return (
    <div>
      <SectionList
        onCreateNew={onCreateNew}
        onStatusChange={onStatusChange}
        triggerEdit={triggerEdit}
        dataSource={sectionList}
        paginationParams={paginationParams}
        handleTableChange={handleTableChange}
      />
      {
        showItemCreation &&
        <CreateSection
          showItemCreation={showItemCreation}
          closeItemCreation={closeItemCreationForm}
          isEditJourney={isEditJourney}
          formData={section}
        />
      }
      <Modal
        title='Activate Section'
        visible={showSectionEnableModal}
        onOk={onModalOk}
        onCancel={onModalCancel}
        okText=' Yes, Activate'
      >
        <p>Are you sure to activate this section ?</p>
      </Modal>
      <Modal
        title='Deactivate Section'
        visible={showSectionDisableModal}
        onOk={onModalOk}
        onCancel={onModalCancel}
        okText='Yes, Deactivate'
      >
        <p>Are you sure to Deactivate this section ?</p>
      </Modal>
    </div>
  )
}

export default ManageSectionScreen
